import { useQuery, useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getDataSources,
  previewReport,
  exportReport,
  downloadReport,
  aggregateReport,
  aiGenerateConfig,
  aiGenerateInsights,
  type ReportPreviewConfig,
  type ReportExportConfig,
  type AggregateConfig,
  type ReportFilter,
  type AggregateSummary,
} from '@/services/PagesServices/CustomReportBuilderService';

const KEYS = {
  dataSources: ['report-data-sources'] as const,
  preview: (config: ReportPreviewConfig) => ['report-preview', config] as const,
  aggregate: (config: AggregateConfig) => ['report-aggregate', config] as const,
};

export function useDataSources() {
  return useQuery({
    queryKey: KEYS.dataSources,
    queryFn: async () => {
      const result = await getDataSources();
      if (!result.success || !result.data) {
        throw new Error(result.error?.message ?? 'Failed to fetch data sources');
      }
      return result.data;
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function usePreviewReport() {
  return useMutation({
    mutationFn: async (config: ReportPreviewConfig) => {
      const result = await previewReport(config);
      if (!result.success || !result.data) {
        throw new Error(result.error?.message ?? 'Failed to preview report');
      }
      return result.data;
    },
    onError: () => {
      toast.error('Insufficient data to generate report');
    },
  });
}

export function useExportReport() {
  return useMutation({
    mutationFn: async (config: ReportExportConfig) => {
      const result = await exportReport(config);
      if (!result.success || !result.data) {
        throw new Error(result.error?.message ?? 'Failed to export report');
      }

      const blob = await downloadReport(result.data.fileName);
      if (!blob) {
        throw new Error('Failed to download report file');
      }

      triggerBrowserDownload(blob, result.data.fileName);
    },
    onSuccess: () => {
      toast.success('Report exported successfully');
    },
    onError: () => {
      toast.error('Insufficient data to generate report');
    },
  });
}

export function useAggregateReport() {
  return useMutation({
    mutationFn: async (config: AggregateConfig) => {
      const result = await aggregateReport(config);
      if (!result.success || !result.data) {
        throw new Error(result.error?.message ?? 'Failed to aggregate data');
      }
      return result.data;
    },
    onError: () => {
      toast.error('Insufficient data to generate report');
    },
  });
}

export function useAIGenerateConfig() {
  return useMutation({
    mutationFn: async (prompt: string) => {
      const result = await aiGenerateConfig(prompt);
      if (!result.success || !result.data) {
        throw new Error(result.error?.message ?? 'AI generation failed');
      }
      return result.data;
    },
    onError: () => {
      toast.error('Insufficient data to generate report');
    },
  });
}

export function useAIInsights() {
  return useMutation({
    mutationFn: async (params: {
      dataSource: string;
      fields: string[];
      filters: ReportFilter[];
      sortBy?: string;
      sortOrder?: string;
      chartSummary?: AggregateSummary | null;
    }) => {
      const result = await aiGenerateInsights(params);
      if (!result.success || !result.data) {
        throw new Error(result.error?.message ?? 'Failed to generate insights');
      }
      return result.data;
    },
    onError: () => {
      toast.error('Insufficient data to generate report');
    },
  });
}

function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
